import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiUser, 
  FiUsers, 
  FiMail, 
  FiLock, 
  FiEye, 
  FiEyeOff,
  FiZap,
  FiMessageCircle,
  FiBriefcase,
  FiGlobe,
  FiCheck,
  FiArrowRight,
  FiBookOpen
} from 'react-icons/fi';
import TrustPromise from '../components/TrustPromise';

const RegisterContainer = styled.div`
  min-height: 100vh;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vw, 2rem);
  
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    padding-top: 1.5rem;
  }
`;

const RegisterCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 1200px;
  display: flex;
  min-height: 600px;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const LeftSection = styled.div`
  flex: 1;
  background: #0F766E;
  color: white;
  padding: clamp(2rem, 6vw, 3rem) clamp(1.5rem, 4vw, 2rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-width: 0;
  order: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    order: 2;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const RightSection = styled.div`
  flex: 1;
  padding: clamp(2rem, 6vw, 3rem) clamp(1.5rem, 4vw, 2rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  order: 1;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    order: 1;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  color: white;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  margin-bottom: 2rem;
  text-align: center;
  opacity: 0.9;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const UserTypeTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.4rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.4rem;
  }
`;

const Tab = styled.button`
  flex: 1 1 40%;
  min-width: 0;
  background: ${props => (props.active ? '#0f766e' : 'transparent')};
  border: none;
  color: ${props => (props.active ? 'white' : '#334155')};
  padding: clamp(0.5rem, 2vw, 0.75rem);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: clamp(0.75rem, 2vw, 0.85rem);
  
  &:hover {
    background: ${props => (props.active ? '#0f766e' : '#e2e8f0')};
  }
  
  @media (max-width: 768px) {
    border-radius: 8px;
    padding: 0.875rem;
  }
  
  @media (max-width: 480px) {
    border-radius: 8px;
    padding: 0.75rem;
    font-size: 0.9rem;
  }
`;

const BenefitsList = styled.div`
  margin-top: 2rem;
  position: relative;
  z-index: 1;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const BenefitIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.25rem 0;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.9rem;
  }
`;

const FormTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1f2937;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 0.375rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.25rem;
  }
`;

const FormSubtitle = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  color: #6b7280;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 3vw, 1.5rem);
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(0.75rem, 2vw, 1rem);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.375rem, 1.5vw, 0.5rem);
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: clamp(0.8rem, 2.5vw, 0.9rem);
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const Input = styled.input`
  padding: clamp(0.625rem, 2vw, 0.75rem) clamp(0.75rem, 2.5vw, 1rem);
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0D9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    border-radius: 6px;
  }
`;

const TextArea = styled.textarea`
  padding: clamp(0.625rem, 2vw, 0.75rem) clamp(0.75rem, 2.5vw, 1rem);
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 4.5rem;
  font-family: inherit;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #0D9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: clamp(0.625rem, 2vw, 0.75rem) clamp(0.75rem, 2.5vw, 1rem);
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #0D9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    border-radius: 6px;
  }
`;

const SubmitButton = styled.button`
  background: #0F766E;
  color: white;
  border: none;
  padding: clamp(0.875rem, 2.5vw, 1rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(13, 148, 136, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    margin-top: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    width: 100%;
    border-radius: 8px;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: clamp(1rem, 3vw, 1.5rem);
  color: #6b7280;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  
  a {
    color: #0D9488;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    margin-top: 1rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 0.75rem;
    font-size: 0.8rem;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: clamp(0.75rem, 2vw, 1rem);
  border-radius: 8px;
  font-size: clamp(0.8rem, 2.5vw, 0.9rem);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem;
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
  }
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: clamp(0.75rem, 2vw, 1rem);
  border-radius: 8px;
  font-size: clamp(0.8rem, 2.5vw, 0.9rem);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem;
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
  }
`;

const Register = () => {
  const { userType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const resolveTab = (param, path) => {
    const fromParam = (param || '').toLowerCase();
    if (fromParam === 'student' || path.endsWith('/student')) return 'candidate';
    if (fromParam === 'consultancy' || path.endsWith('/consultancy')) return 'consultancy';
    if (fromParam === 'institution' || path.endsWith('/institution')) return 'institution';
    if (fromParam === 'partner' || fromParam === 'edtech' || path.endsWith('/partner')) return 'partner';
    return 'candidate';
  };

  const [activeTab, setActiveTab] = useState(() => resolveTab(userType, location.pathname));
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    company: '',
    industry: '',
    blurb: '',
    location: '',
    website: '',
    education_level: '',
    cohort_label: '',
    org_kind: 'university',
    staff_role: 'placement_head',
    agreeToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jdData, setJdData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Check for JD upload data from home page
  useEffect(() => {
    const jdUploadData = sessionStorage.getItem('jd_upload_data');
    if (jdUploadData) {
      setJdData(JSON.parse(jdUploadData));
      // Auto-select candidate tab if JD was uploaded
      setActiveTab('candidate');
    }
  }, []);

  // Update activeTab when userType changes from URL
  useEffect(() => {
    setActiveTab(resolveTab(userType, location.pathname));
  }, [userType, location.pathname]);

  const candidateBenefits = [
    {
      icon: <FiZap />,
      title: "Practice Interviews",
      description: "Seven modes: voice, video, written, MCQ, aptitude, coding, typing"
    },
    {
      icon: <FiMessageCircle />,
      title: "Skill Assessment",
      description: "Latest score per interview mode on your profile"
    },
    {
      icon: <FiBriefcase />,
      title: "Job descriptions",
      description: "Parse a JD and get one recommended interview mode"
    },
    {
      icon: <FiArrowRight />,
      title: "Scores that stay",
      description: "Each mode keeps its latest result on your profile"
    }
  ];

  const consultancyBenefits = [
    {
      icon: <FiUsers />,
      title: "CRM pipeline",
      description: "Invite candidates and move stages sourced → placed on /crm"
    },
    {
      icon: <FiBriefcase />,
      title: "CRM analytics",
      description: "Live counts from your managed pipeline — not placement forecasts"
    },
    {
      icon: <FiGlobe />,
      title: "Client Companies",
      description: "Track companies and open roles you work with"
    },
    {
      icon: <FiCheck />,
      title: "Bulk invite",
      description: "Paste or load emails, then invite — export managed CRM as CSV"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
      const isPerson = activeTab === 'candidate';
      const PARTNER_FOCUS_BLURB = {
        coding: 'Coding and software interview prep tutorials',
        aptitude: 'Aptitude and reasoning prep tutorials',
        communication: 'Communication and soft-skills prep tutorials',
        domain: 'Domain-skill interview prep tutorials',
        other: 'Interview prep tutorials',
      };
      const partnerBlurb =
        (formData.blurb || '').trim() ||
        PARTNER_FOCUS_BLURB[formData.industry] ||
        formData.industry;
      const submitData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        first_name: isPerson ? formData.firstName : '',
        last_name: isPerson ? formData.lastName : '',
        phone: formData.phone,
        user_type: isPerson ? 'candidate' : activeTab,
        company_name: formData.company,
        company_website: formData.website,
        company_description: activeTab === 'partner' ? partnerBlurb : formData.industry,
        location: formData.location,
        education_level: formData.education_level,
        institution_name: formData.company,
        cohort_label: formData.cohort_label,
        org_kind: formData.org_kind,
        staff_role: formData.staff_role,
      };

      const result = await register(submitData);

      if (result.success) {
        if (jdData) {
          sessionStorage.removeItem('jd_upload_data');
        }
        navigate('/login', {
          replace: true,
          state: {
            message:
              result.message
              || 'Check your email for a verify link (or open /verify-email and paste the token), then sign in.',
            email: result.email || formData.email,
            firstSession: true,
          },
        });
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const institutionBenefits = [
    {
      icon: <FiBookOpen />,
      title: "Learner roster",
      description: "See students and their interview activity"
    },
    {
      icon: <FiBriefcase />,
      title: "Placement and training",
      description: "Run the cohort without a consultancy CRM"
    },
    {
      icon: <FiUsers />,
      title: "Staff roles",
      description: "Placement heads, training heads, and instructors"
    },
    {
      icon: <FiCheck />,
      title: "Students are not listed for sale",
      description: "Talent search stays off unless a student opts in"
    }
  ];

  const partnerBenefits = [
    {
      icon: <FiBookOpen />,
      title: 'List tutorials by topic',
      description: 'Tag external lessons that map to interview weak topics',
    },
    {
      icon: <FiZap />,
      title: 'Suggested on reports',
      description: 'Approved links can appear on candidate interview reports',
    },
    {
      icon: <FiUsers />,
      title: 'Reach practicing candidates',
      description: 'Show up when candidates need prep after a scored round',
    },
  ];

  const getCurrentBenefits = () => {
    if (activeTab === 'consultancy') return consultancyBenefits;
    if (activeTab === 'institution') return institutionBenefits;
    if (activeTab === 'partner') return partnerBenefits;
    return candidateBenefits;
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <RightSection>
          <FormTitle>
            {activeTab === 'consultancy'
              ? 'Consultancy Registration'
              : activeTab === 'institution'
              ? 'Institution Registration'
              : activeTab === 'partner'
              ? 'EdTech Partner Registration'
              : 'Candidate Registration'}
          </FormTitle>
          <FormSubtitle>
            {activeTab === 'consultancy'
              ? 'Register your firm. We use the company name, not a personal name.'
              : activeTab === 'institution'
              ? 'Register your university or school. We use the institution name, not a personal name.'
              : activeTab === 'partner'
              ? 'Register your EdTech brand. Tutorials stay private until a super admin approves your partner account.'
              : 'Create your account and start practicing in seven interview modes. College and school students use this same form.'}
          </FormSubtitle>

          <UserTypeTabs role="tablist" aria-label="Registration type">
            <Tab
              type="button"
              role="tab"
              aria-selected={activeTab === 'candidate'}
              active={activeTab === 'candidate'}
              onClick={() => navigate('/register/candidate')}
            >
              <FiUser />
              Candidate
            </Tab>
            <Tab
              type="button"
              role="tab"
              aria-selected={activeTab === 'consultancy'}
              active={activeTab === 'consultancy'}
              onClick={() => navigate('/register/consultancy')}
            >
              <FiUsers />
              Consultancy
            </Tab>
            <Tab
              type="button"
              role="tab"
              aria-selected={activeTab === 'institution'}
              active={activeTab === 'institution'}
              onClick={() => navigate('/register/institution')}
            >
              <FiBriefcase />
              Institution
            </Tab>
            <Tab
              type="button"
              role="tab"
              aria-selected={activeTab === 'partner'}
              active={activeTab === 'partner'}
              onClick={() => navigate('/register/partner')}
            >
              <FiBookOpen />
              EdTech
            </Tab>
          </UserTypeTabs>
          
          {jdData && activeTab === 'candidate' && (
            <div style={{
              background: 'rgba(13, 148, 136, 0.1)',
              border: '1px solid rgba(13, 148, 136, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', color: '#0D9488', marginBottom: '0.5rem' }}>
                📄 Job Description Ready for Analysis
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                {jdData.filename} ({Math.round(jdData.size / 1024)} KB)
              </div>
              <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                After registration, we'll analyze this JD and provide personalized preparation suggestions.
              </div>
            </div>
          )}
          
          <Form onSubmit={handleSubmit}>
            {error && (
              <div style={{ 
                background: '#fee2e2', 
                border: '1px solid #f87171', 
                color: '#dc2626', 
                padding: '0.75rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}
            {activeTab === 'candidate' && (
            <FormRow>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                />
              </FormGroup>
            </FormRow>
            )}
            
            <FormRow>
              <FormGroup>
                <Label>Username *</Label>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a password"
                  required
                />
                <span onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#0D9488' }}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </FormGroup>
              <FormGroup>
                <Label>Confirm Password *</Label>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required
                />
                <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#0D9488' }}>
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
              />
            </FormGroup>
            
            {activeTab === 'consultancy' && (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>Company Name *</Label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter company name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Industry *</Label>
                    <Select
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select industry</option>
                      <option value="technology">Technology</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance</option>
                      <option value="education">Education</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="retail">Retail</option>
                      <option value="consulting">Consulting</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>Location</Label>
                    <Input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Enter company location"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Website</Label>
                    <Input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="Enter company website"
                    />
                  </FormGroup>
                </FormRow>
              </>
            )}

            {activeTab === 'partner' && (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>EdTech brand name *</Label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Academy / publisher name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Focus *</Label>
                    <Select
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select focus</option>
                      <option value="coding">Coding / software</option>
                      <option value="aptitude">Aptitude / reasoning</option>
                      <option value="communication">Communication</option>
                      <option value="domain">Domain skills</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label>Website</Label>
                    <Input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://…"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Location</Label>
                    <Input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Optional"
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Public blurb (optional)</Label>
                    <TextArea
                      rows={3}
                      name="blurb"
                      value={formData.blurb}
                      onChange={handleInputChange}
                      placeholder="Short honest pitch for /partners. If empty, we use your focus area."
                    />
                  </FormGroup>
                </FormRow>
              </>
            )}

            {activeTab === 'candidate' && (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>College / school</Label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Optional, if you are a student"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Education level</Label>
                    <Select
                      name="education_level"
                      value={formData.education_level}
                      onChange={handleInputChange}
                    >
                      <option value="">Select level</option>
                      <option value="high_school">High school</option>
                      <option value="diploma">Diploma</option>
                      <option value="undergraduate">Undergraduate</option>
                      <option value="postgraduate">Postgraduate</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Label>Batch / cohort</Label>
                  <Input
                    type="text"
                    name="cohort_label"
                    value={formData.cohort_label}
                    onChange={handleInputChange}
                    placeholder="e.g. 2026 CSE"
                  />
                </FormGroup>
              </>
            )}

            {activeTab === 'institution' && (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>Institution name *</Label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="University or school name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Type *</Label>
                    <Select
                      name="org_kind"
                      value={formData.org_kind}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="university">University / college</option>
                      <option value="school">School</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Label>Your role *</Label>
                  <Select
                    name="staff_role"
                    value={formData.staff_role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="placement_head">Placement head</option>
                    <option value="training_head">Training head</option>
                    <option value="instructor">Instructor</option>
                    <option value="owner">Admin / owner</option>
                  </Select>
                </FormGroup>
              </>
            )}
            
            <SubmitButton type="submit" disabled={loading}>
              {loading
                ? 'Creating Account...'
                : `Join as ${
                    activeTab === 'consultancy'
                      ? 'Consultancy'
                      : activeTab === 'institution'
                      ? 'Institution'
                      : activeTab === 'partner'
                      ? 'EdTech partner'
                      : 'Candidate'
                  }`}
            </SubmitButton>
          </Form>
          
          <LoginLink>
            Already have an account? <Link to="/login">Sign in here</Link>
          </LoginLink>
        </RightSection>

        <LeftSection>
          <Title>Join EnterWeU</Title>
          <Subtitle>
            {activeTab === 'consultancy'
              ? 'Grow your consultancy business'
              : activeTab === 'institution'
              ? 'Run placement and training for your institution'
              : activeTab === 'partner'
              ? 'List tutorials that help candidates close weak topics'
              : 'Start your journey to interview success'}
          </Subtitle>
          <TrustPromise tone="light" align="left" />
          <BenefitsList>
            {getCurrentBenefits().map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  {benefit.icon}
                </BenefitIcon>
                <BenefitText>
                  <h4>{benefit.title}</h4>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </LeftSection>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register; 